import React, { PureComponent } from "react";
import { BetQLSportItem, NameKeyPair } from "src/common-services";

import styles from "./styles.scss";
import TrendingSummary from "./TrendingSummary";
import TrendingList from "./TrendingList";
import { ButtonGroupItem } from "../../ButtonGroup";

/**
 * Properties needed for this component
 *
 * @param homeTeam The home team gql object
 * @param awayTeam The away team gql object
 * @param currentSportObj The current sport
 * @param eventTrends The array of eventTrends gql object
 */
interface Props {
  homeTeam: any;
  awayTeam: any;
  currentSportObj: BetQLSportItem;
  gameId: string;
  freeGame: boolean;
}

/**
 * TrendsOverViewState state representation
 *
 * @param selectedTrendIndex The current selected trend index
 */
interface State {
  filteredTrends: Array<any>;
  currentTrendType: NameKeyPair;
  selectedTeamId: string;
  trendCountData: any;
  currentTrendCountData: any;
  teamSelected: string;
}

/**
 * Deault trend setting to hold default settings for trends data
 */
interface DefaultTrendSettings {
  team: any;
  trendType: NameKeyPair;
}

/**
 * Component to display list of trends and it's summary data
 */
class TrendsOverView extends PureComponent<Props, State> {
  loaded = false;

  constructor(props: Props) {
    super(props);
    this.state = this.getInitialState();
  }

  componentDidUpdate() {
    if (!this.loaded) {
      this.loaded = true;
      // This is out of scope for this ticket
      // eslint-disable-next-line
      this.setState(this.getInitialState());
    }
  }

  getInitialState = (): State => {
    const trendCountData: any = {};

    trendCountData[this.props.homeTeam.id] = this._buildTrendCountByTypes(
      this.props.homeTeam?.trends || [],
      this.props.homeTeam.id
    );

    trendCountData[this.props.awayTeam.id] = this._buildTrendCountByTypes(
      this.props.awayTeam?.trends || [],
      this.props.awayTeam.id
    );

    const defaultTrendSettings: DefaultTrendSettings = this._createDefaultTrendSettings(
      this.props.homeTeam,
      this.props.awayTeam,
      this.props.currentSportObj.trendTypes,
      trendCountData
    );

    return {
      teamSelected:
        this.props.homeTeam.id === defaultTrendSettings.team.id
          ? this.props.homeTeam.lastName
          : this.props.awayTeam.lastName,
      selectedTeamId: defaultTrendSettings.team.id,
      filteredTrends: this._filterTrends(
        defaultTrendSettings.team.trends,
        defaultTrendSettings.team.id,
        defaultTrendSettings.trendType
      ),
      currentTrendType: defaultTrendSettings.trendType,
      trendCountData,
      currentTrendCountData: trendCountData[defaultTrendSettings.team.id] || {},
    };
  };

  /**
   * Select default trend settings that has more than 1 trends on it
   */
  _createDefaultTrendSettings = (
    homeTeam: any,
    awayTeam: any,
    trendTypes: NameKeyPair[],
    trendCountData: any
  ): DefaultTrendSettings => {
    const settings: DefaultTrendSettings = {
      team: homeTeam,
      trendType: trendTypes[0],
    };
    const teams: any[] = [homeTeam, awayTeam];
    let found: boolean = false;

    for (let i = 0; i < trendTypes.length && !found; i++) {
      for (let j = 0; j < teams.length; j++) {
        const team: any = teams[j];
        if (trendCountData[team.id][trendTypes[i].key] > 0) {
          settings.team = team;
          settings.trendType = trendTypes[i];
          found = true;
          break;
        }
      }
    }

    return settings;
  };

  /**
   * Build a map of trends count by the trend type, output data will be: { ats: 10, ml: 20 }
   *
   * @param eventTrends List of trends to process
   */
  _buildTrendCountByTypes = (eventTrends: Array<any>, teamId: string) => {
    const teamTrendCountData: any = {};

    this.props.currentSportObj.trendTypes.forEach((trendType: NameKeyPair) => {
      teamTrendCountData[trendType.key] = this._filterTrends(eventTrends, teamId, trendType).length;
    });

    return teamTrendCountData;
  };

  /**
   * Helper method to filter by both team id and trendtype
   *
   * @param eventTrends The array of event trend to filter on
   * @param teamId Team id to filter on
   * @param trendType The trendtype to filter on
   */
  _filterTrends = (eventTrends: Array<any> = [], teamId: string, trendType: NameKeyPair) => {
    return eventTrends.filter((trend) =>
      trend.rating < 0
        ? trend.teamId !== teamId && trend.trendType === trendType.key
        : trend.teamId === teamId && trend.trendType === trendType.key
    );
  };

  /**
   * Filter by the trend by teamId
   *
   * @param teamId Team id to filter on
   */
  onTeamSelection = (teamId: string) => {
    this.setState({
      selectedTeamId: teamId,
      filteredTrends: this._filterTrends(
        teamId === this.props.homeTeam.id ? this.props.homeTeam.trends : this.props.awayTeam.trends,
        teamId,
        this.state.currentTrendType
      ),
      currentTrendCountData: this.state.trendCountData[teamId] || {},
      teamSelected: teamId === this.props.homeTeam.id ? this.props.homeTeam.lastName : this.props.awayTeam.lastName,
    });
  };

  /**
   * Filter the list of trends by the selected trend type: OU, ML
   *
   * @param currentTrendType The current trend type we're filtering on
   */
  onTrendSelected = (currentTrendType: NameKeyPair) => {
    this.setState({
      currentTrendType,
      filteredTrends: this._filterTrends(
        this.state.selectedTeamId === this.props.homeTeam.id ? this.props.homeTeam.trends : this.props.awayTeam.trends,
        this.state.selectedTeamId,
        currentTrendType
      ),
    });
  };

  _buildTrendButtons = (currentTrendCountData: any): ButtonGroupItem[] => {
    const buttonGroupList: ButtonGroupItem[] = [];

    for (let i = 0; i < this.props.currentSportObj.trendTypes.length; i++) {
      let buttonGroupItem: ButtonGroupItem = this.props.currentSportObj.trendTypes[i];

      const count: number = currentTrendCountData[buttonGroupItem.key] || 0;

      buttonGroupList[i] = {
        ...buttonGroupItem,
        labelSelected: (
          <div className="trending__button-group-container">
            {count === 0 ? null : (
              <span className="trending__count-container trending__count-container--selected">{count}</span>
            )}
            {buttonGroupItem.name}
          </div>
        ),
        labelSelectedInverse: (
          <div className="trending__button-group-container">
            {count === 0 ? null : (
              <span className="trending__count-container trending__count-container--selected--inverse">{count}</span>
            )}
            {buttonGroupItem.name}
          </div>
        ),
        label: (
          <div className="trending__button-group-container">
            {count === 0 ? null : <span className="trending__count-container">{count}</span>}
            {buttonGroupItem.name}
          </div>
        ),
      };
    }

    return buttonGroupList;
  };

  render() {
    const { filteredTrends, currentTrendType, currentTrendCountData, selectedTeamId, teamSelected } = this.state;
    const { freeGame, gameId, currentSportObj, homeTeam, awayTeam } = this.props;

    return (
      <div className="trending__container">
        <TrendingSummary
          freeGame={freeGame}
          onTeamSelected={this.onTeamSelection}
          homeTeam={homeTeam}
          awayTeam={awayTeam}
          currentSportObj={currentSportObj}
          currentTrendType={currentTrendType}
          trends={filteredTrends}
          homeTeamSelected={selectedTeamId === homeTeam.id}
        />
        <TrendingList
          freeGame={freeGame}
          gameId={gameId}
          homeTeam={homeTeam}
          awayTeam={awayTeam}
          teamSelected={teamSelected}
          onTrendSelected={this.onTrendSelected}
          currentTrendType={currentTrendType}
          trends={filteredTrends}
          currentSportObj={currentSportObj}
          trendButtonItemList={this._buildTrendButtons(currentTrendCountData)}
        />
        <style jsx global>
          {styles}
        </style>
      </div>
    );
  }
}

export default TrendsOverView;
